import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { ConfirmDialog, Tabs, TabsList, TabsTrigger } from 'components'
import { useAnalytics, useIsMobile } from 'hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { SCHEDULE_TOOL_ACTIVITY_EVENT } from '../assistant-chat-events'
import { IdeaCard } from '../components/IdeaCard'
import { MobilePageHeader } from '../components/MobilePageHeader'
import { PostModal } from '../components/PostModal'
import {
  isSyntheticPostIdea,
  selectPostsPageItems,
  syntheticProjectIdToCreativeId,
  type PostsFilter,
} from '../selectors/posts-page-items'
import { useCreativeStore } from '../store/creative-store'
import { useIdeaStore, type PostIdea } from '../store/idea-store'
import { editIdeaInComposer } from '../utils/edit-in-composer'

const FILTER_OPTIONS: Array<{ value: PostsFilter; label: string }> = [
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'live', label: 'Live' },
]

const EMPTY_COPY: Record<PostsFilter, string> = {
  draft: 'No drafts yet. Pick a topic on Home to start a post.',
  scheduled: 'Nothing scheduled. Posts you schedule will land here.',
  live: 'Nothing live yet. Posts you publish will land here.',
}

export function PostsPage() {
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const isMobile = useIsMobile()

  const ideas = useIdeaStore(s => s.ideas)
  const fetchIdeas = useIdeaStore(s => s.fetchIdeas)
  const regenerateIdea = useIdeaStore(s => s.regenerateIdea)

  const deletePost = useIdeaStore(s => s.deletePost)

  const creatives = useCreativeStore(s => s.creatives)
  const creativesLoaded = useCreativeStore(s => s.loaded)
  const fetchCreatives = useCreativeStore(s => s.fetchCreatives)
  const deleteCreative = useCreativeStore(s => s.deleteCreative)

  const [filter, setFilter] = useState<PostsFilter>('draft')
  const [activeIdea, setActiveIdea] = useState<PostIdea | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<PostIdea | null>(null)

  // Mount: kick both stores so the unified grid has data from both worlds.
  useEffect(() => {
    fetchIdeas()
    if (!creativesLoaded) void fetchCreatives()
  }, [fetchIdeas, fetchCreatives, creativesLoaded])

  // The assistant dock floats OVER this page; when the agent schedules or
  // moves a post the draft/scheduled buckets must follow live (same contract
  // as the Calendar page and Home week floor).
  useEffect(() => {
    const handleScheduleToolActivity = () => {
      fetchIdeas()
      void fetchCreatives()
    }
    window.addEventListener(SCHEDULE_TOOL_ACTIVITY_EVENT, handleScheduleToolActivity)
    return () =>
      window.removeEventListener(SCHEDULE_TOOL_ACTIVITY_EVENT, handleScheduleToolActivity)
  }, [fetchIdeas, fetchCreatives])

  const items = useMemo(
    () => selectPostsPageItems({ ideas, creatives, filter }),
    [ideas, creatives, filter]
  )

  // A failed post no longer auto-retries on load (removed with HomePage's copy).
  // It burned a credit every visit and re-drafting masked the terminal failed
  // state as an eternal "Drafting…". The server reclaim marks stuck generations
  // failed + refunds; the card's explicit Retry is the user's to tap.

  // Page-viewed event fires once per filter change so we can compare
  // draft / scheduled / live engagement over time.
  useEffect(() => {
    const draftItems = selectPostsPageItems({ ideas, creatives, filter: 'draft' })
    const scheduledItems = selectPostsPageItems({ ideas, creatives, filter: 'scheduled' })
    const liveItems = selectPostsPageItems({ ideas, creatives, filter: 'live' })
    track(AnalyticsEvents.Posts.POSTS_PAGE_VIEWED, {
      post_count: draftItems.length + scheduledItems.length + liveItems.length,
      draft_count: draftItems.length,
      scheduled_count: scheduledItems.length,
      live_count: liveItems.length,
      platform: isMobile ? 'mobile' : 'desktop',
    })
    // Intentionally only fire when stores hydrate, not on every filter flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ideas.length, creatives.length])

  const handleFilter = useCallback(
    (next: PostsFilter) => {
      setFilter(next)
      track(AnalyticsEvents.Posts.POSTS_FILTER_TAPPED, { filter: next })
    },
    [track]
  )

  const handlePost = useCallback((idea: PostIdea) => {
    setActiveIdea(idea)
  }, [])

  const handleRegenerate = useCallback(
    (idea: PostIdea) => {
      // Synthetic ideas wrap a standalone Creative — no project to regenerate
      // against. Bail silently; the AssetMedia retry button is the only caller.
      if (isSyntheticPostIdea(idea.projectId)) return
      track(AnalyticsEvents.Home.HOME_IDEA_CARD_REGENERATE_TAPPED, {
        idea_id: idea.projectId,
        shape: idea.shape,
        source: 'posts',
      })
      void regenerateIdea(idea.projectId)
    },
    [regenerateIdea, track]
  )

  const handleEdit = useCallback(
    (idea: PostIdea) => {
      track(AnalyticsEvents.Home.HOME_POST_MODAL_EDIT_TAPPED, {
        platform: idea.confirmedPlatforms?.[0] ?? idea.primaryPlatform,
        project_id: idea.projectId,
      })
      editIdeaInComposer(navigate, idea, {
        source: 'idea_card',
        entrySource: 'posts_drafts',
      })
    },
    [navigate, track]
  )

  const handleClosePostModal = useCallback(() => {
    setActiveIdea(null)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    const idea = confirmDelete
    if (!idea) return
    try {
      // Synthetic ideas wrap a standalone Creative (no real Project) — delete
      // the Creative directly; real posts delete the Project so the FK cascade
      // takes every platform sibling with it.
      const creativeId = syntheticProjectIdToCreativeId(idea.projectId)
      if (creativeId) {
        await deleteCreative(creativeId)
      } else {
        await deletePost(idea.projectId)
      }
    } catch (err) {
      console.error('Failed to delete post:', err)
      toast.error("We couldn't delete this post. Please try again.")
      throw err
    }
  }, [confirmDelete, deleteCreative, deletePost])

  return (
    <div className={isMobile ? 'flex h-full flex-col' : 'mx-auto w-full max-w-[1100px] px-6 py-8'}>
      {isMobile ? (
        <MobilePageHeader title="Posts" subtitle="Everything you've drafted and published" />
      ) : (
        <div className="mb-6">
          <span className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
            Posts
          </span>
          <h1 className="mt-1 text-[24px] font-semibold tracking-[-0.01em] text-paper sm:text-[28px]">
            Everything you've drafted and published
          </h1>
        </div>
      )}

      <div className={`mb-6 ${isMobile ? 'px-4 pt-4' : ''}`}>
        <Tabs value={filter} onValueChange={v => handleFilter(v as PostsFilter)}>
          <TabsList variant="pill">
            {FILTER_OPTIONS.map(opt => (
              <TabsTrigger key={opt.value} value={opt.value} variant="pill">
                {opt.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {items.length === 0 ? (
        <div
          className={`flex flex-col items-center justify-center py-20 text-center ${isMobile ? 'px-4' : ''}`}
        >
          <p className="text-[14px] text-paper-dim">{EMPTY_COPY[filter]}</p>
          {filter === 'draft' && (
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-ink-on-acid mt-4 rounded-[9px] bg-acid px-4 py-2 text-[13.5px] font-semibold shadow-[var(--shadow-acid-cta)] transition-all hover:-translate-y-px hover:shadow-[var(--shadow-acid-cta-hover)]"
            >
              Pick a topic on Home
            </button>
          )}
        </div>
      ) : (
        <div
          className={`grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 ${isMobile ? 'px-4 pb-4' : ''}`}
        >
          {items.map(idea => (
            <IdeaCard
              key={idea.projectId}
              idea={idea}
              onPost={handlePost}
              onRegenerate={handleRegenerate}
              onEdit={() => handleEdit(idea)}
              onDelete={() => setConfirmDelete(idea)}
            />
          ))}
        </div>
      )}

      <PostModal idea={activeIdea} onClose={handleClosePostModal} entrySource="posts_drafts" />

      <ConfirmDialog
        open={confirmDelete !== null}
        onOpenChange={open => {
          if (!open) setConfirmDelete(null)
        }}
        title="Delete this post?"
        description="All platform variants for this post will be permanently deleted. This cannot be undone."
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
